/**
 * V1/HARON/{PRODUCT}/{serial}/sensors — from device, RETAINED.
 *
 * The controller's inventory of the physical slots it has. This is the ONLY
 * source of slot numbers: the controller assigns them, so the app can never
 * invent one — it can only name a slot that was reported here, and
 * `apply_config`'s `switches[].slot` (rest/device-ops.ts) refers straight back
 * to it.
 *
 * Published retained, so a late subscriber gets the current set with no
 * request — MQTT has no "GET". The device republishes at boot and on every
 * sensor connect/disconnect, never per motion event.
 *
 * The same document is served over the LAN by `GET /api/sensors` on the
 * controller, so both legs feed the app one shape.
 */
import { z } from 'zod';
import { Iso8601Schema } from '../common/timestamp.js';
/** What kind of thing is wired to a slot. `switch` is a wall switch/button;
 *  `ldr` is an ambient-light sensor. Free-form beyond these two so a new
 *  sensor kind does not need a contract release before it can be reported. */
export const SensorSlotTypeSchema = z.string().min(1).max(32);
export const SensorSlotSchema = z.object({
    /** Controller-assigned, stable across reboots (the controller persists its
     *  slot ↔ sensor table), and the key a physical press resolves through.
     *  Numbers count PER TYPE: switch slots (S1, S2, …) and LDR slots
     *  (L1, L2, …) are independent sequences, so `(slot, type)` is the unique
     *  key of an entry — a switch and an LDR may both be slot 1. */
    slot: z.number().int().min(1),
    type: SensorSlotTypeSchema,
    /** Whether the sensor is currently answering. A slot that goes quiet stays
     *  in the list as `online: false` rather than vanishing, so Setup can tell
     *  an unplugged sensor from one that was never there. */
    online: z.boolean(),
    /** LDR slots only: the current ambient-light reading on the app-facing
     *  0–10 scale (0 = dark, 10 = bright). The controller scales it down from
     *  the sensor's raw 0–255 bus frame and republishes this inventory when the
     *  SCALED value changes — at most 11 levels, so the traffic stays trivial.
     *  Absent for switch slots and for an LDR that has not reported yet. */
    value: z.number().int().min(0).max(10).optional(),
});
/**
 * REPLACE-IN-FULL: `sensors` is the COMPLETE current set. An empty array is a
 * real answer — "nothing connected" — not an error and not "unknown".
 */
export const CeilingSensorInventorySchema = z.object({
    ts: Iso8601Schema,
    sensors: z.array(SensorSlotSchema).max(32),
});
//# sourceMappingURL=sensors.js.map